(() => {
  'use strict';

  const SEARCH_MODAL = 'details-modal.header__search';
  const BUTTON_SELECTOR = '[data-ca-visual-search-trigger]';

  const hostname = window.location.hostname;

  const isLocalDevelopment =
    hostname === '127.0.0.1' ||
    hostname === 'localhost' ||
    hostname.endsWith('.myshopify.com');

  const API_ENDPOINT = isLocalDevelopment
    ? 'http://localhost:3000/api/visual-search-proxy'
    : '/apps/visual-search';

  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const ACCEPTED_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif'
  ]);

  const ACCEPTED_EXTENSIONS = /\.(jpe?g|png|webp|gif)$/i;

  let fileInput = null;
  let activeTrigger = null;
  let isSearching = false;

  function getFileInput() {
    if (fileInput) {
      return fileInput;
    }

    fileInput = document.createElement('input');

    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg,image/png,image/webp,image/gif';
    fileInput.tabIndex = -1;
    fileInput.setAttribute('aria-hidden', 'true');
    fileInput.className = 'ca-visual-search-file-input';

    const modal = activeTrigger?.closest('details-modal');

  if (modal) {
    modal.appendChild(fileInput);
  } else {
    document.body.appendChild(fileInput);
  }

    fileInput.addEventListener('change', () => {
      const file = fileInput.files?.[0];

      if (!file) {
        return;
      }

      handleSelectedFile(file);
    });

    return fileInput;
  }

  function validateFile(file) {
    if (!file) {
      return 'Please select an image.';
    }

    const typeAllowed = ACCEPTED_TYPES.has(file.type);
    const extensionAllowed = ACCEPTED_EXTENSIONS.test(file.name);

    if (!typeAllowed && !extensionAllowed) {
      return 'Please select a JPG, PNG, WEBP, or GIF image.';
    }

    if (file.size > MAX_FILE_SIZE) {
      return 'Please select an image smaller than 10 MB.';
    }

    return null;
  }

  function setButtonState(loading) {
    if (!activeTrigger) {
      return;
    }

    activeTrigger.disabled = loading;
    activeTrigger.setAttribute('aria-busy', loading ? 'true' : 'false');

    const text = activeTrigger.querySelector(
      '.ca-visual-search-trigger__text'
    );

    if (text) {
      text.textContent = loading
        ? 'Searching...'
        : 'Search via image';
    }
  }

  function positionSearchModalLoader() {
  const modal = activeTrigger?.closest('details-modal');

  if (!modal) {
    return;
  }

  const loader = modal.querySelector(
    '[data-ca-visual-search-loader]'
  );

  const searchForm = modal.querySelector(
    'form.search .field'
  );

  const modalContent = modal.querySelector(
    '.search-modal__content'
  );

  if (!loader || !searchForm || !modalContent) {
    return;
  }

  const searchRect = searchForm.getBoundingClientRect();
  const contentRect = modalContent.getBoundingClientRect();

  loader.style.position = 'absolute';
  loader.style.left =
    `${searchRect.left - contentRect.left}px`;
  loader.style.top =
    `${searchRect.bottom - contentRect.top + 18}px`;
}

function setSearchModalState(searching) {
    const modal =
      activeTrigger?.closest('details-modal');

    if (!modal) {
      return;
    }

    modal.classList.toggle(
      'ca-visual-search--searching',
      searching
    );

    modal.setAttribute(
      'aria-busy',
      searching ? 'true' : 'false'
    );

    let loader =
      modal.querySelector(
        '[data-ca-visual-search-loader]'
      );

    if (searching) {

      if (!loader) {
        loader =
          document.createElement('div');

        loader.className =
          'ca-visual-search-loader';

        loader.setAttribute(
          'data-ca-visual-search-loader',
          ''
        );

        loader.setAttribute(
          'role',
          'status'
        );

        loader.setAttribute(
          'aria-live',
          'polite'
        );

        loader.innerHTML = `
          <div class="ca-visual-search-loader__visual">
            <span class="ca-visual-search-loader__ring"></span>
            <span class="ca-visual-search-loader__spark"></span>
          </div>

          <div class="ca-visual-search-loader__copy">
            <span class="ca-visual-search-loader__title">
              Analyzing your image
            </span>

            <span class="ca-visual-search-loader__dots">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </div>

          <div
            class="ca-visual-search-loader__track"
            aria-hidden="true"
          >
            <span class="ca-visual-search-loader__bar"></span>
            <span class="ca-visual-search-loader__shine"></span>
          </div>
        `;

        const trigger =
          modal.querySelector(
            '.ca-visual-search-trigger'
          );

        if (trigger) {
          trigger.insertAdjacentElement(
            'afterend',
            loader
          );
        }
        else {
          modal.appendChild(loader);
        }
      }

      loader.hidden = false;

    }
    else {

      if (loader) {
        loader.hidden = true;
      }
    }
  }

  function showStatus(message, isError = false) {
    let status = document.querySelector('.ca-visual-search-status');

    if (!status) {
      status = document.createElement('div');
      status.className = 'ca-visual-search-status';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      document.body.appendChild(status);
    }

    status.textContent = message;
    status.dataset.error = isError ? 'true' : 'false';
    status.hidden = false;
  }

  function hideStatus() {
    const status = document.querySelector('.ca-visual-search-status');

    if (status) {
      status.hidden = true;
    }
  }

  async function searchByImage(file) {
    const formData = new FormData();

    formData.append('image', file, file.name);

    setButtonState(true);
    setSearchModalState(true);
    showStatus('Finding visually similar products...');

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      });

      const rawText = await response.text();

      let data;

      try {
        data = JSON.parse(rawText);
      } catch {
        throw new Error(
          'The visual search service returned an invalid response.'
        );
      }

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.error ||
          `Visual search failed with HTTP ${response.status}.`
        );
      }

      if (!Array.isArray(data.results)) {
        throw new Error(
          'Visual search returned an invalid results format.'
        );
      }

      try {
        sessionStorage.setItem(
          'ca_visual_search_results',
          JSON.stringify(data.results)
        );

        sessionStorage.setItem(
          'ca_visual_search_source',
          'image'
        );
      } catch (storageError) {
        console.warn(
          '[Customize Awards] Could not store visual search results:',
          storageError
        );

        throw new Error(
          'Could not prepare the visual search results page.'
        );
      }

      window.location.href = '/search?visual_search=1';
    } catch (error) {
      console.error(
        '[Customize Awards] Visual search failed:',
        error
      );

      const message =
        error instanceof Error
          ? error.message
          : 'Visual search failed. Please try again.';

      showStatus(message, true);
      setButtonState(false);
      setSearchModalState(false);

      window.setTimeout(hideStatus, 5000);
    } finally {
      isSearching = false;

      if (fileInput) {
        fileInput.value = '';
      }
    }
  }

  async function handleSelectedFile(file) {
    const validationError = validateFile(file);

    if (validationError) {
      showStatus(validationError, true);

      if (fileInput) {
        fileInput.value = '';
      }

      window.setTimeout(hideStatus, 5000);

      return;
    }

    if (isSearching) {
      return;
    }

    isSearching = true;

    await searchByImage(file);
  }

  function openFilePicker(trigger) {
    if (isSearching) {
      return;
    }

    activeTrigger = trigger;

    const input = getFileInput();

    input.value = '';
    input.click();
  }

  function initialiseTrigger(trigger) {
    if (trigger.dataset.caVisualSearchReady === 'true') {
      return;
    }

    trigger.dataset.caVisualSearchReady = 'true';

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      openFilePicker(trigger);
    });
  }

  function initialise() {
    document
      .querySelectorAll(BUTTON_SELECTOR)
      .forEach(initialiseTrigger);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise);
  } else {
    initialise();
  }

  const observer = new MutationObserver(() => {
    initialise();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();

window.addEventListener('resize', positionSearchModalLoader);




